// src/components/Layout.js
import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  CssBaseline,
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import {
  ExpandLess,
  ExpandMore,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Collapse } from '@mui/material';

// Drawer width
const drawerWidth = 240;

// AppBar – no more width/margin shifting
const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: 'transparent', // dark
  color: 'theme.palette.text.primary',           // white
  boxShadow: 'none',
  borderBottom: 'none',
}));

// Drawer header
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 2),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
  color: '#FEFEFE',
}));

// Main content – always offset on desktop, never “slides”
const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingLeft: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  marginLeft: 0,
}));


const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false); // only matters on mobile
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const avatarInitial =
    user?.name?.[0]?.toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    '?';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />

      {/* Top bar */}
      <AppBar position="fixed">
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, fontWeight: 600, pr: 1 }}
          >
            Envoy Dashboard
          </Typography>

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar
              src={user?.profile_image_url || ''}
              alt={user?.name || user?.email || 'User avatar'}
              sx={{
                width: 44,
                height: 44,
                bgcolor: "#2563eb",
                border: "2px solid rgba(255,255,255,0.20)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
              }}
            >
              {avatarInitial}
            </Avatar>
          </IconButton>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            // background: 'linear-gradient(180deg, #023E8A 0%, #0077B6 100%)',
            background: '#231f20',
            color: '#FEFEFE',
            borderRight: 'none',
            overflowX: 'hidden',
          },
        }}
        variant={isMobile ? 'temporary' : 'permanent'}
        anchor="left"
        open={isMobile ? open : true}
        onClose={handleDrawerClose}
        ModalProps={{ keepMounted: true }}
      >
        <DrawerHeader>
          {/* <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Envoy
          </Typography> */}
          {isMobile && (
            <IconButton onClick={handleDrawerClose} sx={{ color: '#FEFEFE' }}>
              <ChevronLeftIcon />
            </IconButton>
          )}
        </DrawerHeader>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

        <List sx={{ mt: 1, overflowX: 'hidden' }}>
          <ListItem
            button
            onClick={() => navigate('/dashboard')}
            sx={{
              borderRadius: 2,
              mx: 1,
              mb: 0.5,
              width: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(254,254,254,0.12)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#FEFEFE', minWidth: 40 }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItem>

          {user?.role === 'admin' && (
            <ListItem
              button
              onClick={() => navigate('/users')}
              sx={{
                borderRadius: 2,
                mx: 1,
                mb: 0.5,
                width: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(254,254,254,0.12)',
                },
              }}
            >
              <ListItemIcon sx={{ color: '#FEFEFE', minWidth: 40 }}>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary="User Management" />
            </ListItem>
          )}
          {/* Settings Section */}
          <ListItem
            button
            onClick={() => setSettingsOpen(!settingsOpen)}
            sx={{
              borderRadius: 2,
              mx: 1,
              mb: 0.5,
              width: 'auto',
              '&:hover': { backgroundColor: 'rgba(254,254,254,0.12)' },
            }}
          >
            <ListItemIcon sx={{ color: '#FEFEFE', minWidth: 40 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
              {settingsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
           {/* Dropdown Items */}
          <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                onClick={() => navigate('/settings/pos')}
                sx={{ pl: 6, py: 0.5, '&:hover': { backgroundColor: 'rgba(254,254,254,0.12)' } }}
              >
                <ListItemText primary="Store Details" />
              </ListItem>
              <ListItem
                button
                onClick={() => navigate('/settings/email-templates')}
                sx={{ pl: 6, py: 0.5, '&:hover': { backgroundColor: 'rgba(254,254,254,0.12)' } }}
              >
                <ListItemText primary="Email Templates" />
              </ListItem>
            </List>
          </Collapse>
        </List>
      </Drawer>

      {/* Main content */}
      <Main>
        <Toolbar /> {/* pushes content below AppBar */}
        <Outlet />
      </Main>
    </Box>
  );
};

export default Layout;
